import { useMemo, useRef, useState, useEffect } from "react";
import { CREWS } from "./data/crews";
import { ROLES } from "./roles";
import { getCrewDestination } from "./scoring";
import { drawCrew } from "./draw";

const JOURNEY_DELAY_MS = 1100;

// Single pending timeout, cleared on unmount.
const useTimer = () => {
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  return {
    schedule: (fn, ms) => {
      timer.current = setTimeout(fn, ms);
    },
    cancel: () => clearTimeout(timer.current),
  };
};

// Current crew + crews already drawn this game (each crew max once per game).
const useCrewDraw = () => {
  const [crew, setCrew] = useState(null);
  const usedCrewIds = useRef(new Set());
  const draw = () => {
    const selectedCrew = drawCrew(CREWS, usedCrewIds.current);
    usedCrewIds.current.add(selectedCrew.id);
    setCrew(selectedCrew);
  };
  const resetDraw = () => {
    usedCrewIds.current = new Set();
    setCrew(null);
  };
  return { crew, setCrew, draw, resetDraw };
};

export function useGame() {  const [assignments, setAssignments] = useState({});
  const [phase, setPhase] = useState("ready"); // ready | spinning | choose | complete | journey
  const [rerollUsed, setRerollUsed] = useState(false);
  const [lastFilled, setLastFilled] = useState(null);
  const [reachedIndex, setReachedIndex] = useState(0);
  const { crew, setCrew, draw, resetDraw } = useCrewDraw();
  const timer = useTimer();
  const usedPeople = useMemo(() => new Set(Object.values(assignments).map((c) => c.person)), [assignments]);
  const isAvailable = (c) => !usedPeople.has(c.person);
  const spin = () => {
    draw();
    setPhase("spinning");
  };
  const finishSpin = () => setPhase("choose");
  const reroll = () => {
    if (rerollUsed || phase !== "choose") return;
    setRerollUsed(true);
    spin();
  };
  const assign = (character, roleKey) => {
    if (phase !== "choose" || assignments[roleKey] || !isAvailable(character)) return;
    const next = { ...assignments, [roleKey]: character };
    setAssignments(next);
    setLastFilled(roleKey);
    setCrew(null);
    if (Object.keys(next).length < ROLES.length) return setPhase("ready");
    setReachedIndex(getCrewDestination(next));
    setPhase("complete");
    timer.schedule(() => setPhase("journey"), JOURNEY_DELAY_MS);
  };
  const reset = () => {
    timer.cancel();
    resetDraw();
    setAssignments({});
    setRerollUsed(false);
    setLastFilled(null);
    setReachedIndex(0);
    setPhase("ready");
  };
  return {
    assignments, phase, crew, rerollUsed, lastFilled, reachedIndex,
    filledCount: Object.keys(assignments).length,
    isAvailable, spin, finishSpin, reroll, assign, reset,
  };
}
